import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  TextField,
  Grid,
  Typography,
  Button,
  DialogActions,
  TablePagination
} from "@mui/material";
import MoreMenu from "../Admin-PaymentMethod/MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import { getPendingPaymentMethods, PaymentMethodAction, PendingPaymentMethodAction, getPaymentMethodWithActions } from "../../services/payment";
import { Log } from "../../services/services.js";
import { useFormik } from "formik";
import { StyledTableRow } from "../TableRow.js";
import CircularLoader from "../circularLoader.js";
import FormDialog from "../FormDialog.js";
import Label from "../Label.js";
import FormSearch from "../FormSearch.js";
import { CustomTableCell } from "../TableCell";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import CommonDialog from "../CustomDialog";
import { styles } from "../../utils/styles";
import PaymentMethodDetails from "./Dialogs/PaymentMethodDetails";
import * as yup from "yup";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "name", label: "Payment Method" },
  { id: "action", label: "Action" },
  { id: "details" },
];

function PaymentMethodPending({ userRole }) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [loaded, setLoaded] = useState(false);
  
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [pendingDetails, setPendingDetails] = useState(null);

  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [paymentMethodList, setPaymentMethodList] = useState({ results: [], count: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPendingPaymentMethods({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...defaultValue, ...queryParams },
    });
    setPagination({
      page: queryParams.page || 1,
      page_size: queryParams.page_size || 50,
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = applySortFilter(
    paymentMethodList.results || [],
    order,
    orderBy
  );

  const renderData = data => {
    if (!data || !data.results) return;
    const newData = data.results.map(item => ({
      ...item,
    }))
    setPaymentMethodList({ count: data.count, results: newData });
  }

  const isItemNotFound = (paymentMethodList.results || []).length === 0;

  const [selectedId, setSelectedId] = useState();
  const handleCloseDialog = (shouldResetDetails) => {
    setOpenRejectDialog(false);
    if (shouldResetDetails === true) setPendingDetails(null);
  };

  useEffect(() => {
    queryPage(pagination);
  }, []);

  const RejectPendingMethodDialog = ({
    open,
    onClose,
    selectedId,
    setPaymentMethodList,
    setLoaded,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Reject] - Reject Payment method by Id: ${selectedId}`, values);
        await PendingPaymentMethodAction(selectedId, "reject", setPaymentMethodList, setLoaded, values.reason);
        handleClose(true);
      }
    });

    const handleClose = (isSubmitted) => {
      onClose(isSubmitted);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Reject this payment method?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Grid container sx={{ alignItems: "center" }} spacing={3}>
          <Grid item md={5} xs={5}>
            <Typography variant="subtitle1">Reason *</Typography>
          </Grid>
          <Grid item md={7} xs={7}>
            <TextField
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              required
              placeholder="Please enter your reason"
              error={Boolean(touched.reason && errors.reason)}
              helperText={touched.reason && errors.reason}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  };

  return (
    <Box>

      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map(({ id, name, action }) => {
                    return (
                      <StyledTableRow
                        hover
                        key={id}
                        sx={{ cursor: "default" }}
                      >
                        <TableCell children={name} id={id} />
                        <TableCell children={action} id={id} />
                        <TableCell>
                          <Button
                            variant="text"
                            color="secondary"
                            onClick={() => {
                              setGlobalLoading(true);
                              getPaymentMethodWithActions(id, setPendingDetails, () => setGlobalLoading(false)
                              );
                            }}
                          >
                            View Detail
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={paymentMethodList?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          <RejectPendingMethodDialog
            open={openRejectDialog}
            onClose={handleCloseDialog}
            selectedId={selectedId}
            setPaymentMethodList={renderData}
            setLoaded={setLoaded}
          />
        </>
      )}

      {pendingDetails && (
        <CommonDialog
          title="Payment Method Details"
          onClose={() => setPendingDetails(null)}
          renderActions={() =>
            userRole.includes("Approve/Reject") && (
              <DialogActions>
                <Button
                  variant="outlined"
                  // color="inherit"
                  onClick={() => {
                    setOpenRejectDialog(true);
                    setSelectedId(pendingDetails.id);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setGlobalLoading(true);
                    Log.info(
                      `Click button [Approve] - Approve Payment Method by Id: ${pendingDetails.id}`
                    );
                    await PendingPaymentMethodAction(
                      pendingDetails.id,
                      "approve",
                      renderData,
                      setLoaded
                    );
                    setPendingDetails(null);
                    setGlobalLoading(false);
                  }}
                  sx={{ ...styles.customButton }}
                >
                  Approve
                </Button>
              </DialogActions>
            )
          }
        >
          <PaymentMethodDetails detailsData={pendingDetails} />
        </CommonDialog>
      )}
    </Box>
  );
}

export default PaymentMethodPending;
